import { useEffect, useRef } from 'react';
import type { AssistantRuntime } from '@assistant-ui/react';

export interface UseResumeInFlightRunOptions {
  runtime: AssistantRuntime;
  /**
   * The in-flight run detected by `useConversationRestore` when the page
   * reloaded mid-run (last persisted message is a user turn carrying a runId).
   * Undefined when no run is in flight.
   */
  inFlightRun?: { runId: string; parentMessageId: string };
  /** Gate so a hidden/background surface doesn't kick off a resume. */
  enabled?: boolean;
}

/**
 * Re-attaches the conversation to a run that is still executing server-side
 * after a page reload. The assistant turn persists only at run_finished, so on
 * reload the thread ends at the user turn and the live trace appears lost.
 *
 * This starts a fresh assistant message (`thread.startRun`) parented to that
 * user turn and tags it with `runConfig.custom.resumeRunId`. The adapter's
 * resume branch (see {@link createAsyncInvokeRun}) skips the invoke POST, seeds
 * the chain-of-thought from the run's persisted steps, and streams the rest
 * live — so the message is genuinely `running` and the CoT resumes exactly like
 * a normal foreground run.
 *
 * Fires exactly ONCE per runId: the ref guard survives re-renders and the
 * StrictMode double-mount (staging runs a dev build), so a second mount for the
 * same run is a no-op while a genuinely new in-flight run re-arms it.
 */
export function useResumeInFlightRun({
  runtime,
  inFlightRun,
  enabled = true,
}: UseResumeInFlightRunOptions): void {
  const resumedFor = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (!enabled || !inFlightRun) return;
    if (resumedFor.current === inFlightRun.runId) return;
    // Never fire while this thread already has a run in flight: the foreground
    // adapter is attached and streaming, so there is nothing to resume — and
    // `thread.startRun` would make assistant-ui abort the live generator
    // (a plain internal abort), tearing down a healthy run mid-stream. This
    // matters when a conversation refetch lands mid-run: the assistant turn
    // only persists at run_finished, so the restore sees a trailing user turn
    // and reports an "in-flight run" that is in fact already attached here.
    if (runtime.thread.getState().isRunning) return;
    resumedFor.current = inFlightRun.runId;

    try {
      runtime.thread.startRun({
        parentId: inFlightRun.parentMessageId,
        runConfig: { custom: { resumeRunId: inFlightRun.runId } },
      });
    } catch (err) {
      // The parent message must exist in the thread for startRun to resolve.
      // It comes from the same conversation restore that produced inFlightRun,
      // so this should not happen — but never crash the surface over it.
      console.warn('[useResumeInFlightRun] failed to resume run', inFlightRun.runId, err);
    }
  }, [runtime, inFlightRun, enabled]);
}
